// src/services/analytics.js
//
// Usage analytics for Story Threads Vibe, on the fleet's consent-aware GA4
// rail (@ddd/analytics; reference implementation apps/profound-thinking,
// brief docs/ANALYTICS_CONSENT_ROLLOUT.md).
//
// Nothing is measured until the visitor says yes, on the first-visit notice
// or the Your data page. @ddd/analytics only injects the gtag loader after
// consent, so before that the app makes no analytics request of any kind.
//
// The measurement ID is a literal on purpose (public by nature; an env var
// silently turns analytics off in any build without a .env). G-BPGJ5KKPMD is
// this app's own GA4 web stream, supplied by Nigel on PR #608 (26 September
// 2026). It replaces the legacy index.html tag for G-05505S7G8J, the property
// shared with Inner Voice, MindEase and MindShield Vibe, which loaded with no
// consent.
import { initAnalytics, setAnalyticsConsent, getStoredConsent, trackEvent } from '@ddd/analytics';

export const MEASUREMENT_ID = 'G-BPGJ5KKPMD';

// The app's own record of the decision. Preferences in AppContext are keyed
// per user (vibe_preferences_guest, vibe_preferences_<uid>) and not readable
// before React mounts, so the analytics choice keeps a key of its own. It
// starts with 'story-threads-vibe_' so "Delete everything" clears it.
export const PREFERENCE_KEY = 'story-threads-vibe_analytics';

/** The visitor's stored analytics decision. Defaults to false: opt-in, never opt-out. */
export function hasAnalyticsPreference() {
  try {
    return localStorage.getItem(PREFERENCE_KEY) === 'granted';
  } catch {
    return false;
  }
}

/** Record a decision in both places: the rail's own key and ours. */
export function recordAnalyticsDecision(granted) {
  try {
    localStorage.setItem(PREFERENCE_KEY, granted ? 'granted' : 'denied');
  } catch {
    /* storage unavailable: the in-page decision still applies */
  }
  setAnalyticsConsent(granted);
}

/**
 * Call once at startup, before React mounts. Loads GA4 only if the visitor
 * has already opted in; otherwise it arms the helper and waits.
 */
export function startAnalytics() {
  initAnalytics({ measurementId: MEASUREMENT_ID, appId: 'story-threads-vibe' });
  // Record a consent state only when a real decision exists. Writing
  // 'denied' for a visitor who has never been asked would erase the
  // never-asked state the first-visit notice keys on.
  if (getStoredConsent() !== null || hasAnalyticsPreference()) {
    setAnalyticsConsent(hasAnalyticsPreference());
  }
}

/**
 * Screen changes. Views here are React state, not URLs, so GA4's enhanced
 * measurement never sees them. View ids only ('modules', 'toolkit'…), and a
 * no-op until consent (trackEvent checks the loader).
 */
export function trackScreen(viewId) {
  trackEvent('screen_view', { screen_name: viewId });
}

export { setAnalyticsConsent, getStoredConsent };
